import { Urls } from './urls';
import { isDateField } from './utils';

/**
 * Get list of available drivers for a shipment.
 * @param {Object} params Query params
 */
export function getDrivers(params) {
  return new Promise((resolve, reject) => {
    $.ajax(`${Urls['drivers-list']()}?${$.param(params)}`, {
      method: 'GET',
      success: (data) => resolve(data),
      error: (err) => reject(err),
    });
  });
}

/**
 * Trigger API to clear stored default layout settings.
 */
export function resetDefaultLayout(tableType) {
  return new Promise((resolve, reject) => {
    $.ajax({
      method: 'POST',
      data: {table_type: tableType},
      url: Urls['layouts-reset-default'](),
      success: () => resolve(),
      error: () => reject(),
    });
  });
}

/**
 * Get user layouts of the shipment table.
 * Exclude default layout.
 * @param {string} tableType Type of the table.
 */
export function getLayouts(tableType) {
  const url = Urls['layouts-list']();

  return new Promise((resolve, reject) => {
    $.ajax(url, {
      type: 'GET',
      dataType: 'json',
      data: {
        'is_default': false,
      },
      success: (response => {
        const layouts = response.filter(layout => layout.table_type === tableType);

        resolve(layouts);
      }),
      error: (err => reject(err)),
    });
  });
}

/**
 * Get default layout of the shipment table.
 * @param {string} tableType Type of the table.
 */
export function getDefaultLayout(tableType) {
  const url = Urls['layouts-list']();

  return new Promise((resolve, reject) => {
    $.ajax(url, {
      type: 'GET',
      dataType: 'json',
      data: {
        'is_default': true,
      },
      success: (response => {
        if (!response.length) {
          resolve(null);

          return;
        }

        const layout = response.find(layout => layout.table_type === tableType);

        if (layout.settings.filters) {
          layout.settings.filters.forEach(filter => {
            if (isDateField(filter.field)) {
              filter.value = new Date(filter.value);
            };
          });
        }

        resolve(layout);
      }),
      error: (err => reject(err)),
    });
  });
}

/**
 * Get default layout of the shipment table.
 */
export function createLayout(layoutData) {
  const url = Urls['layouts-list']();

  return new Promise((resolve, reject) => {
    $.ajax(url, {
      type: 'POST',
      dataType: 'json',
      contentType: 'application/json',
      data: JSON.stringify(layoutData),
      success: (response => resolve(response)),
      error: (err => reject(err)),
    });
  });
}

/**
 * Get default layout of the shipment table.
 */
export function activateLayout(id) {
  const url = Urls.layouts_activate(id);

  return new Promise((resolve, reject) => {
    $.ajax(url, {
      type: 'PUT',
      dataType: 'json',
      contentType: 'application/json',
      success: (response => resolve(response)),
      error: (err => reject(err)),
    });
  });
}

/**
 * Update existent layout of the shipment table.
 * @param {number} id Layout ID.
 * @param {Object} layout Layout config
 */
export function updateLayout(id, layout) {
  const url = Urls.layouts_detail(id);

  return new Promise((resolve, reject) => {
    $.ajax(url, {
      type: 'PATCH',
      dataType: 'json',
      contentType: 'application/json',
      data: JSON.stringify(layout),
      success: () => resolve(),
      error: () => reject(),
    });
  });
}

/**
 * Delete selected layout of the shipment table.
 */
export function deleteLayout(id) {
  const url = Urls.layouts_detail(id);

  return new Promise((resolve, reject) => {
    $.ajax(url, {
      type: 'DELETE',
      dataType: 'json',
      contentType: 'application/json',
      success: (response => resolve(response)),
      error: (err => reject(err)),
    });
  });
}

/**
 * Update outbound_ry (Destination City) of shipment.
 * @param {number} shipmentId Shipment id.
 * @param {string} destCityText Destination City.
 */
export function updateShipmentDestinationCity(shipmentId, destCityText) {
  const url = Urls['shipments-detail'](shipmentId);

  return new Promise((resolve, reject) => {
    $.ajax(url, {
      type: 'PATCH',
      dataType: 'json',
      data: {
        outbound_ry: destCityText,
      },
      success: (response) => resolve(response.driver_data),
      error: (error) => reject(error),
    });
  });
}

/**
 * Create import job.
 *
 * @param {object} formData - Data with the file and status of import job.
 */
export function createImportJob(formData) {
  return new Promise((resolve, reject) => {
    $.ajax({
      method: 'POST',
      url: Urls['import_jobs-list'](),
      processData: false,
      contentType: false,
      data: formData,
      success: (data) => resolve(data),
      error: (error) => reject(error),
    });
  });
}

/**
 * Return status and info about selected Celery task.
 *
 * @param {string} taskId - ID of Celery task.
 */
export function getStatusTask(taskId) {
  return new Promise((resolve, reject) => {
    $.ajax({
      method: 'POST',
      url: Urls['celery-get-task-status'](),
      contentType: 'application/json',
      data: JSON.stringify({'task_id': taskId}),
      success: (task) => resolve(task),
      error: (error) => reject(error),
    });
  });
}

/**
 * Start import job celery task by Id.
 *
 * @param {string} importJobId - Id of created import job.
 */
export function startValidateFile(importJobId) {
  return new Promise((resolve, reject) => {
    $.ajax({
      method: 'POST',
      url: Urls['import_jobs-validate'](importJobId),
      success: (data) => resolve(data),
      error: (error) => reject(error),
    });
  });
}

/**
 * Start import job celery task by Id.
 *
 * @param {string} importJobId - Id of created import job.
 */
export function startImportJob(importJobId) {
  return new Promise((resolve, reject) => {
    $.ajax({
      method: 'POST',
      url: Urls['import_jobs-start'](importJobId),
      success: (data) => resolve(data),
      error: (error) => reject(error),
    });
  });
}

/**
 * Get import job by id.
 *
 * @param {string} importJobId - Id of created import job.
 */
export function getImportJob(importJobId) {
  return new Promise((resolve, reject) => {
    $.ajax({
      method: 'GET',
      url: Urls['import_jobs-detail'](importJobId),
      dataType: 'json',
      success: (data) => resolve(data),
      error: (error) => reject(error),
    });
  });
}

/* ----------------------------------------------------------------------------
* API calls to get report data from the third-party API
* -------------------------------------------------------------------------- */

/**
 * Get report data from UP API.
 *
 * @param {number} id - ID of Shipment.
 * */
export function getUPReport(id) {
  return new Promise((resolve, reject) => {
    $.ajax({
      method: 'GET',
      url: Urls['uprr-get-shipment-report'](id),
      dataType: 'json',
      success: (data) => resolve(data),
      error: (error) => reject(error),
    });
  });
}

/**
 * Get report data from NS Shipments.
 *
 * @param {number} id - ID of Shipment.
 * */
export function getNSReport(id) {
  return new Promise((resolve, reject) => {
    $.ajax({
      method: 'GET',
      url: Urls['ns-get-shipment-report'](id),
      dataType: 'json',
      success: (data) => resolve(data),
      error: (error) => reject(error),
    });
  });
}
